import React, { memo, useCallback, useMemo, useRef } from 'react';
import { type NextBestAction } from '@post-office/confluence-next-best-action';
// import { TestPlacement } from '@post-office/placements--growth/confluence-side-nav/test';
import { Placement as ConfluenceSideNav } from '@post-office/placements/confluence-side-nav';

import {
	PlacementFetchProvider,
	usePlacementPreloadedData,
} from '@atlassian/confluence-post-office-provider/entry-points/preload-placement';

import { buildCreationLink } from '@confluence/create-blank-fabric-page/entry-points/createContentRouteUtils';
import { useCreateSpaceStoreActions } from '@confluence/create-space/entry-points/createSpace';
import { useLoomAiMeetingNotesSideNavCardListener } from '@confluence/experiment-loom-ai-meeting-notes-in-confluence-cross-flow/entry-points/useLoomAiMeetingNotesSideNavCardListener';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { fg } from '@confluence/feature-gating';
import { useRouteActions, useRouteUrl } from '@confluence/route-manager/entry-points/RouteState';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { useTriggerCrossFlowSuccessCheck } from '@confluence/loom-utils/entry-points/useTriggerCrossFlowSuccessCheck';
import { getLogger } from '@confluence/logger';
import { HOME } from '@confluence/named-routes/entry-points/routes';
import { useUniversalCreateActions } from '@confluence/create-blank-fabric-page/entry-points/useUniversalCreate';
import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager/entry-points/storageInstance';

const PO_CONFLUENCE_SIDE_NAV = 'poConfluenceSideNavCreate';

const useIsNav4AndEnabledFF = () => {
	const nav4 = useIsNav4Enabled();
	if (nav4) {
		return fg('post-office-connie-side-nav-v4');
	}

	// If we are not using nav4, return true
	return true;
};

type SideNavClickHandler = (nextBestActionType: NextBestAction, messages: unknown[]) => void;
type SideNavCallbackHandler = (callbackKey: NextBestAction) => Promise<void>;

type PostOfficeConfluenceSideNavPlacementProps = {
	nav4?: boolean;
};

const logger = getLogger('PostOfficeConfluenceSideNavPlacement');

export const PostOfficeConfluenceSideNavPlacement = memo(
	({ nav4 = false }: PostOfficeConfluenceSideNavPlacementProps) => {
		const [preloadedInitialData, setInitialData] = usePlacementPreloadedData('confluence-side-nav');
		const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
		const { push } = useRouteActions();
		const { openDrawer } = useCreateSpaceStoreActions();
		const sideNavRef = useRef(null);
		const { triggerCrossFlowSuccessCheck } = useTriggerCrossFlowSuccessCheck({
			sourceComponent: PO_CONFLUENCE_SIDE_NAV,
		});
		const currentRoute = useRouteUrl({
			selector: (routeUrl: string | undefined) => routeUrl || '',
		});
		const { setIsOpen: setIsUniversalCreateOpen } = useUniversalCreateActions();

		const onDismiss = useCallback(
			(data: unknown) => {
				setInitialData(data);
			},
			[setInitialData],
		);

		const onSideNavClick = useCallback<SideNavClickHandler>(
			(nextBestActionType: NextBestAction, data: unknown) => {
				setInitialData(data);

				switch (nextBestActionType) {
					case 'CREATE_PAGE': {
						push(
							buildCreationLink({
								contentType: 'page',
								source: PO_CONFLUENCE_SIDE_NAV,
							}),
						);
						break;
					}
					case 'CREATE_WHITEBOARD': {
						push(
							buildCreationLink({
								contentType: 'whiteboard',
								source: PO_CONFLUENCE_SIDE_NAV,
							}),
						);
						break;
					}
					case 'CREATE_SPACE': {
						// The second argument for openDrawer is the location string, which can be undefined or 'topCreateNav'
						// Since we're using the side nav to open the drawer, undefined is passed here.
						openDrawer(sideNavRef, undefined);
						break;
					}
					case 'CREATE_WITH_ROVO_NUDGE': {
						void setIsUniversalCreateOpen(true);
						sessionStorage.setItem('aifc-create-spotlight-session-key', 'true');
						break;
					}
					case 'RESURRECTED_USER_NUDGE': {
						push(HOME.toUrl(undefined, { query: { feedType: 'POPULAR' } }));
						break;
					}
					default:
						return;
				}
			},
			[openDrawer, push, setInitialData, setIsUniversalCreateOpen],
		);

		const onCallback = useCallback<SideNavCallbackHandler>(
			async (callbackKey: NextBestAction) => {
				logger.debug`Executing crossflow success check callback for key: ${callbackKey}`;
				switch (callbackKey) {
					case 'LOOM_AI_MEETING_NOTES': {
						// Handle the callback for the Loom AI Meeting Notes action
						logger.debug`Executing crossflow success check callback for Loom AI Meeting Notes`;
						triggerCrossFlowSuccessCheck({
							onCrossflowFailure: () => {
								logger.debug`[LoomAiMeetingNotesBanner] Cross-flow failure detected via callback`;
							},
						});
						break;
					}
					default:
						return;
				}
			},
			[triggerCrossFlowSuccessCheck],
		);

		const memoizedPlacementProps = useMemo(
			() => ({
				preloadedInitialData,
				onDismiss,
				onSideNavClick,
				onCallback,
				currentRoute,
				disableInitialData: Boolean(preloadedInitialData),
			}),
			[preloadedInitialData, onDismiss, onSideNavClick, onCallback, currentRoute],
		);

		const isNav4AndEnabled = useIsNav4AndEnabledFF();

		/**
		 * This is here to explicitly trigger and enable the loom_ai_meeting_notes_in_confluence message
		 * when a relevant user interaction has occured. It is feature gated internally to no-op if disabled
		 */
		useLoomAiMeetingNotesSideNavCardListener({ readFromLocalStorage: true });

		/**
		 * This is memoized to avoid any fetch calls getting cancelled inside `ConfluenceSideNav` due to
		 * rerenders from changing context values (.ie usePostOfficeSideNavMessages).
		 */
		const PlacementInternals = useMemo(
			() => (
				<PlacementFetchProvider>
					{/* @ts-expect-error: Props are not type checked */}
					<ConfluenceSideNav {...memoizedPlacementProps} />
				</PlacementFetchProvider>
			),
			[memoizedPlacementProps],
		);

		if (!fg('post-office-connie-side-nav-killswitch') || !isNav4AndEnabled) {
			return <></>;
		}

		if (fg('post-office-confluence-side-nav-placement-rollout')) {
			return nav4 ? (
				<div
					data-testid="confluence-side-nav-placement"
					data-vc="confluence-side-nav-placement"
					{...ssrPlaceholderIdProp}
				>
					{PlacementInternals}
				</div>
			) : (
				<div
					data-testid="confluence-side-nav-placement"
					data-vc="confluence-side-nav-placement"
					{...ssrPlaceholderIdProp}
				>
					{PlacementInternals}
				</div>
			);
		}

		return <></>;
	},
);
